import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PIcon } from '@porsche-design-system/components-react';

import { Tooltip } from '../Tooltip';
import { CharacterCounter } from './CharacterCounter';
import { HistoryToolbarButtons } from './HistoryToolbarButtons';
import {
  AlignToolbarButtons,
  BackgroundColorToolbarControl,
  BasicMarksToolbarButtons,
  FontSizeToolbarControl,
  ImageToolbarButtons,
  IndentToolbarButtons,
  LinkToolbarButtons,
  ListToolbarButtons,
  StrikethroughToolbarButtons,
  TableToolbarButtons,
  TextColorToolbarControl,
} from './plugins';
import { hasToolbarFeature, type EditorToolbarPreset } from './toolbarPresets';

export const Container = styled('div', {
  base: {
    display: 'flex',
    paddingBottom: 'sm',
    alignItems: 'center',
    overflowX: 'auto',
  },
});

const Separator = styled('div', {
  base: {
    width: 1,
    mx: 'xs',
    my: 'xs',
    backgroundColor: 'border.contrastLower',
    alignSelf: 'stretch',
  },
});

const Spacer = styled('div', {
  base: {
    flex: 1,
  },
});

const ToolTipContent = styled('div', {
  base: {
    maxWidth: '260px',
    fontSize: 'xs',
    lineHeight: 'normal',
  },
});

const GdprHint = styled('div', {
  base: {
    paddingLeft: 'sm',
  },
});

export const Toolbar = ({
  hasCharactersCounter,
  gdprTooltip,
  imageUpload,
  toolbarPreset = 'default',
  i18n,
}: {
  hasCharactersCounter?: boolean;
  gdprTooltip: string;
  imageUpload?: {
    onUploadImage: (
      fileOrData: File | string,
      opt: { onProgress?: (value: number) => void },
    ) => Promise<string>;
    onCancelImageUpload?: () => void;
    allowedImageTypes?: { type: string; extension: string }[];
    allowPublicImageUpload: 'required' | 'allowed' | 'disallowed';
    i18n?: {
      imageUploadHeading?: string;
      uploadButtonText?: string;
      resetButtonText?: string;
      uploadErrorHeading?: string;
      uploadErrorDescription?: string;
      dropzoneDescription?: string;
      dragDescription?: string;
      allowedTypesDescription?: string;
      publicImageUploadText?: string;
    };
  };
  toolbarPreset?: EditorToolbarPreset;
  i18n?: {
    boldLabel?: string;
    underlineLabel?: string;
    italicLabel?: string;
    strikethroughLabel?: string;
    alignLeftLabel?: string;
    alignCenterLabel?: string;
    alignRightLabel?: string;
    alignJustifyLabel?: string;
    outdentLabel?: string;
    indentLabel?: string;
    insertLinkLabel?: string;
    applyLinkLabel?: string;
    removeLinkLabel?: string;
    sanitizeLinkInstruction?: string;
    bulletedListLabel?: string;
    numberedListLabel?: string;
    insertTableLabel?: string;
    columnLabel?: string;
    rowLabel?: string;
    undoLabel?: string;
    redoLabel?: string;
    fontSizeLabel?: string;
    textColorLabel?: string;
    backgroundColorLabel?: string;
    clearTextColorLabel?: string;
    clearBackgroundColorLabel?: string;
  };
}) => {
  return (
    <Container>
      <BasicMarksToolbarButtons
        boldLabel={i18n?.boldLabel}
        underlineLabel={i18n?.underlineLabel}
        italicLabel={i18n?.italicLabel}
      />
      {hasToolbarFeature(toolbarPreset, 'strikethrough') && (
        <StrikethroughToolbarButtons label={i18n?.strikethroughLabel} />
      )}
      <Separator />
      {hasToolbarFeature(toolbarPreset, 'align') && (
        <>
          <AlignToolbarButtons
            leftLabel={i18n?.alignLeftLabel}
            centerLabel={i18n?.alignCenterLabel}
            rightLabel={i18n?.alignRightLabel}
            justifyLabel={i18n?.alignJustifyLabel}
          />
          <Separator />
        </>
      )}
      {hasToolbarFeature(toolbarPreset, 'indent') && (
        <>
          <IndentToolbarButtons
            outdentLabel={i18n?.outdentLabel}
            indentLabel={i18n?.indentLabel}
          />
          <Separator />
        </>
      )}
      {hasToolbarFeature(toolbarPreset, 'link') && (
        <>
          <LinkToolbarButtons
            insertLinkLabel={i18n?.insertLinkLabel}
            applyLinkLabel={i18n?.applyLinkLabel}
            removeLinkLabel={i18n?.removeLinkLabel}
            sanitizeLinkInstruction={i18n?.sanitizeLinkInstruction}
          />
          <Separator />
        </>
      )}
      <ListToolbarButtons
        bulletedListLabel={i18n?.bulletedListLabel}
        numberedListLabel={i18n?.numberedListLabel}
      />
      <Separator />
      {hasToolbarFeature(toolbarPreset, 'fontSize') && (
        <>
          <FontSizeToolbarControl label={i18n?.fontSizeLabel} />
          <Separator />
        </>
      )}
      {hasToolbarFeature(toolbarPreset, 'textColor') && (
        <>
          <TextColorToolbarControl
            label={i18n?.textColorLabel}
            clearLabel={i18n?.clearTextColorLabel}
          />
          <Separator />
        </>
      )}
      {hasToolbarFeature(toolbarPreset, 'backgroundColor') && (
        <>
          <BackgroundColorToolbarControl
            label={i18n?.backgroundColorLabel}
            clearLabel={i18n?.clearBackgroundColorLabel}
          />
          <Separator />
        </>
      )}
      {hasToolbarFeature(toolbarPreset, 'table') && (
        <>
          <TableToolbarButtons
            insertTableLabel={i18n?.insertTableLabel}
            columnLabel={i18n?.columnLabel}
            rowLabel={i18n?.rowLabel}
          />
          <Separator />
        </>
      )}
      {imageUpload && (
        <>
          <ImageToolbarButtons {...imageUpload} />
          <Separator />
        </>
      )}
      <HistoryToolbarButtons
        undoLabel={i18n?.undoLabel}
        redoLabel={i18n?.redoLabel}
      />

      <Spacer />

      {hasCharactersCounter && <CharacterCounter />}

      <GdprHint>
        <Tooltip
          content={<ToolTipContent>{gdprTooltip}</ToolTipContent>}
          placement="left"
        >
          <PIcon name="information" />
        </Tooltip>
      </GdprHint>
    </Container>
  );
};
